import styles from './CheckoutInfoPage.module.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import { Button } from '@clover-web-lib-sprout/button';
import { useTranslation } from 'react-i18next';
import { TextV2 } from 'components/TextV2/TextV2';
import { FormWrapper } from 'components/Form/FormWrapper';
import { LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import {
  businessNameCharPattern,
  fdmpEmailPattern,
  namePattern,
  usZipCodePattern,
} from 'components/Contact/regex';
import { FooterLinks } from '../CheckoutEmailAndInfoPage/FooterLinks/FooterLinks';
import { useCheckout } from 'components/Checkout/CheckoutProvider';
import { CHECKOUT_INFO_PAGE_TEST_ID } from './CheckoutInfoPage.const';
import { CheckoutInfographic } from '../CheckoutInfographic/CheckoutInfographic';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { CheckoutRoutePath } from '../Checkout';
import { useHistory } from 'react-router-dom';
import { validateZipCode } from 'utils/validateZipCode';
import { ZipModal } from '../ZipModal';
import { isAccountOnlyCart } from 'components/Cart/utils/getCartProductsAndAccessoriesUtils';
import { Installments } from 'components/Cart/OrderSummary/types';
import { IUserInfo } from '../UseCheckout';
import { useCartContext } from 'components/Cart/hooks/useCartContext';
import { IMerchantProspectData, useFiMerchant } from 'hooks/useFiMerchant';

const FULL_NAME_MAX_LENGTH = 23;
const FORM_ID = 'info-form';
const subscriptionPurchaseTypes = [Installments.SL36, Installments.SL48];

export interface ICheckoutInfoPageFields {
  emailAddress: string;
  confirmEmailAddress: string;
  firstName: string;
  lastName: string;
  businessName: string;
  businessZipCode: string;
}

type defaultValuesType = Partial<IUserInfo & IMerchantProspectData>;

const createDefaultValues = (inputValues: defaultValuesType) => {
  const {
    emailAddress = '',
    confirmEmailAddress = '',
    firstName = '',
    lastName = '',
    businessName = '',
    businessZipCode = '',
    zipCode = '',
  } = inputValues;

  const confirmEmail = confirmEmailAddress || emailAddress;
  const businessZip = businessZipCode || zipCode;

  return {
    emailAddress,
    confirmEmailAddress: confirmEmail,
    firstName,
    lastName,
    businessName,
    businessZipCode: businessZip,
  };
};

export const CheckoutInfoPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [zipCodeCheckFailed, setZipCodeCheckFailed] = useState(false);
  const { t } = useTranslation();
  const { userInfo, setUserInfo, selectedPurchaseType } = useCheckout();
  const { cart } = useCartContext();
  const { subscriberRefId, merchantProspectData } = useFiMerchant();
  const history = useHistory();
  const isNoHardwareCart = isAccountOnlyCart(cart);
  const isSubscriptionPurchaseType: boolean = subscriptionPurchaseTypes.includes(selectedPurchaseType);

  const defaultValuesPrefill = subscriberRefId ? merchantProspectData ?? {} : userInfo ?? {};

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    setError,
    clearErrors,
    formState: { errors, isSubmitted },
  } = useForm<ICheckoutInfoPageFields>({
    mode: 'onSubmit',
    criteriaMode: 'firstError',
    defaultValues: createDefaultValues(defaultValuesPrefill),
  });

  const validateNames = (firstName: string, lastName: string) => {
    if (firstName.length + lastName.length > FULL_NAME_MAX_LENGTH) return t('tooLongFullName');
    return true;
  };

  const currentFirstNameValue = watch('firstName');
  const currentLastNameValue = watch('lastName');

  const validateFirstName = (value: string) => validateNames(value, currentLastNameValue);

  const validateLastName = (value: string) => validateNames(currentFirstNameValue, value);

  const validateBusinessZipCode = (value: string) =>
    validateZipCode(value, isSubscriptionPurchaseType, setError, t, setShowModal, setZipCodeCheckFailed);

  const currentEmailValue = watch('emailAddress');

  useEffect(() => {
    if (isSubmitted) trigger('firstName');
  }, [trigger, currentLastNameValue, isSubmitted]);

  useEffect(() => {
    if (isSubmitted) trigger('lastName');
  }, [trigger, currentFirstNameValue, isSubmitted]);

  useEffect(() => {
    if (isSubmitted) trigger('confirmEmailAddress');
  }, [trigger, currentEmailValue, isSubmitted]);

  const onSubmit = (values: ICheckoutInfoPageFields) => {
    setUserInfo({ phoneNumber: '', ...values });
    history.push(redesignV2RoutePath.PURCHASE + CheckoutRoutePath.MOBILE_PHONE);
  };

  const onModalContinueClick = useCallback(() => {
    if (errors?.businessZipCode) clearErrors('businessZipCode');
  }, [clearErrors, errors]);

  const preventDefault = (e: { preventDefault: () => void }) => {
    e.preventDefault();
  };

  return (
    <section className={styles.checkoutInfoPage}>
      <header className={styles.header}>
        <div className={styles.headerTitles}>
          <TextV2 tagName="h2" variant="h2-bold" value={t('checkout-info-page.title')} />
          <TextV2
            className={styles.subtitle}
            tagName="h5"
            variant="body"
            value={
              isNoHardwareCart
                ? t('checkout-info-page.subtitle-account-only')
                : t('checkout-info-page.subtitle-hardware')
            }
          />
        </div>
        <CheckoutInfographic />
      </header>

      <div className={styles.requiredFieldWrapper}>
        <TextV2 tagName="h5" variant="body-small" value={t('checkout-info-page.required-field')} />
      </div>
      <section className={styles.formContainer}>
        <FormWrapper>
          <FormControl fullWidth>
            <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.inputGroup}>
                <TextV2 tagName="h5" variant="h5-medium" value={t('checkout-info-page.info-title')} />
                <div className={styles.twoFieldsOnLine}>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{
                      required: t('firstName.required'),
                      pattern: {
                        value: namePattern,
                        message: t('firstName.invalid'),
                      },
                      validate: validateFirstName,
                    }}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        className={styles.halfField}
                        autoFocus
                        id="first-name"
                        inputProps={{ 'data-testid': CHECKOUT_INFO_PAGE_TEST_ID.firstName }}
                        inputRef={ref}
                        name="firstName"
                        label={`${t('firstName.label')}*`}
                        helperText={errors?.firstName?.message}
                        error={!!errors?.firstName}
                        variant="outlined"
                      />
                    )}
                  />
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{
                      required: t('lastName.required'),
                      pattern: {
                        value: namePattern,
                        message: t('lastName.invalid'),
                      },
                      validate: validateLastName,
                    }}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        className={styles.halfField}
                        id="last-name"
                        inputProps={{ 'data-testid': CHECKOUT_INFO_PAGE_TEST_ID.lastName }}
                        inputRef={ref}
                        name="lastName"
                        label={`${t('lastName.label')}*`}
                        helperText={errors?.lastName?.message}
                        error={!!errors?.lastName}
                        variant="outlined"
                      />
                    )}
                  />
                </div>
                <Controller
                  name="businessName"
                  control={control}
                  rules={{
                    required: t('businessName.required'),
                    minLength: {
                      value: 2,
                      message: t('valid-business-minimum-length'),
                    },
                    maxLength: {
                      value: 24,
                      message: t('valid-business-maximum-length'),
                    },
                    pattern: {
                      value: businessNameCharPattern,
                      message: t('valid-business-name'),
                    },
                  }}
                  render={({ field: { ref, ...rest } }) => (
                    <TextField
                      {...rest}
                      id="business-name"
                      inputProps={{ 'data-testid': CHECKOUT_INFO_PAGE_TEST_ID.businessName }}
                      inputRef={ref}
                      name="businessName"
                      label={`${t('businessName.label')}*`}
                      helperText={errors?.businessName?.message}
                      error={!!errors?.businessName}
                      variant="outlined"
                    />
                  )}
                />
                <Controller
                  name="businessZipCode"
                  control={control}
                  rules={{
                    required: t('businessZipCode.required'),
                    pattern: {
                      value: usZipCodePattern,
                      message: t('businessZipCode.invalid'),
                    },
                    validate: validateBusinessZipCode,
                  }}
                  render={({ field: { ref, ...rest } }) => (
                    <TextField
                      {...rest}
                      id="business-zip-code"
                      inputProps={{ 'data-testid': CHECKOUT_INFO_PAGE_TEST_ID.businessZipCode }}
                      inputRef={ref}
                      name="businessZipCode"
                      label={`${t('businessZipCode.label')}*`}
                      helperText={errors?.businessZipCode?.message}
                      error={!!errors?.businessZipCode}
                      variant="outlined"
                    />
                  )}
                />
                <Controller
                  name="emailAddress"
                  control={control}
                  rules={{
                    required: t('emailAddress.required'),
                    pattern: {
                      value: fdmpEmailPattern,
                      message: t('emailAddress.invalid'),
                    },
                  }}
                  render={({ field: { ref, ...rest } }) => (
                    <TextField
                      {...rest}
                      id="email-address"
                      inputProps={{ 'data-testid': CHECKOUT_INFO_PAGE_TEST_ID.emailAddress }}
                      name="emailAddress"
                      inputRef={ref}
                      label={`${t('emailAddress.label')}*`}
                      error={!!errors?.emailAddress}
                      helperText={errors?.emailAddress?.message}
                      variant="outlined"
                      onCopy={preventDefault}
                      onPaste={preventDefault}
                    />
                  )}
                />
                <Controller
                  name="confirmEmailAddress"
                  control={control}
                  rules={{
                    required: t('confirmEmailAddress.required'),
                    validate: {
                      match: value => value === currentEmailValue || t('confirmEmailAddress.noMatch'),
                    },
                  }}
                  render={({ field: { ref, ...rest } }) => (
                    <TextField
                      {...rest}
                      id="confirm-email-address"
                      inputProps={{ 'data-testid': CHECKOUT_INFO_PAGE_TEST_ID.confirmEmailAddress }}
                      inputRef={ref}
                      name="confirmEmailAddress"
                      label={`${t('confirmEmailAddress.label')}*`}
                      helperText={errors?.confirmEmailAddress?.message}
                      error={isSubmitted && !!errors?.confirmEmailAddress}
                      variant="outlined"
                      onCopy={preventDefault}
                      onPaste={preventDefault}
                    />
                  )}
                />
              </div>
              <TextV2
                className={styles.greyBody}
                tagName="p"
                variant="small"
                value={t('checkout-info-page.email-description')}
              />
            </form>
          </FormControl>
        </FormWrapper>
      </section>
      <div>
        <Button
          data-testid={CHECKOUT_INFO_PAGE_TEST_ID.submitButton}
          type="submit"
          variant={LinkVariant.PRIMARY}
          form={FORM_ID}
        >
          {t('continue')}
        </Button>
      </div>
      <ZipModal
        modalState={[showModal, () => setShowModal(false)]}
        onContinue={onModalContinueClick}
        isServiceUnavailable={zipCodeCheckFailed}
      />
      <FooterLinks />
    </section>
  );
};
